import { useState, useEffect, useMemo } from "react";
import { Typography, Box, Grid, CircularProgress } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { DataTabs } from "../LabTabs";
import { useNavigate } from "react-router-dom";
import { formatNumber } from "../../Utils/utils";
import { useUser } from "../../contexts/UserContext";
import { isTestEnv } from "../../globals";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import AddTaskIcon from "@mui/icons-material/AddTask";
import { NotesAccordion } from "../Tasks";
import { AddTaskDialog } from "./addTaskDialog";
const ObligoExceptionDetails = ({ details, isLoading }) => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedOrderNotes, setSelectedOrderNotes] = useState(null); // Store selected order notes
  const [DOrders, setDOrders] = useState([]); // State for orders
  const [addTaskDialogOpen, setAddTaskDialogOpen] = useState(false);
  const [selectedOrderForTask, setSelectedOrderForTask] = useState(null);

  // Initialize DOrders with details when it's available
  useEffect(() => {
    if (details && details.length > 0) {
      setDOrders(details);
    }
  }, [details]);

  isTestEnv &&
    console.log("userObligoExceptionDetails", user, details, isLoading);

  const handleAddTaskToOrder = (orderRow) => {
    console.log("handleAddTaskToOrder", orderRow);
    setSelectedOrderForTask(orderRow);
    setAddTaskDialogOpen(true);
  };

  const handleTaskAdded = (newTask) => {

    const updatedOrder = {
      ...selectedOrderForTask,
      GENCUSTNOTES_SUBFORM: [
        ...(selectedOrderForTask.GENCUSTNOTES_SUBFORM || []),
        newTask,
      ],
    };

    // Update the orders state
    setDOrders((prevOrders) => {
      const updatedOrders = prevOrders.map((order) => {
        if (order.ORDNAME === selectedOrderForTask.ORDNAME) {
          return updatedOrder;
        }
        return order;
      });
      return updatedOrders;
    });

    setAddTaskDialogOpen(false);

    setTimeout(() => {
      setSelectedOrderNotes(updatedOrder);
      setOpenPopup(true);
    }, 100);
  };

  const handleOpenPopup = (order) => {
    setSelectedOrderNotes(order);
    setOpenPopup(true);
  };
  const handleClosePopup = () => {
    setOpenPopup(false);
    setSelectedOrderNotes(null);
  };

  const handleRowClick = (params) => {
    isTestEnv && console.log("params", params);
    const custname = params.row.ELD_PCUSTNAME
      ? params.row.ELD_PCUSTNAME
      : params.row.CUSTNAME;
    navigate(`/ObligoException/${custname}`);
  };

  const dataGridColumns = [
    {
      field: "CUSTNAME",
      headerName: "מספר לקוח",
      cellClassName: "first-column",
    },
    { field: "CDES", headerName: "שם לקוח" },
    {
      field: "ORDNAME",
      headerName: "מספר הזמנה",
    },
    {
      field: "CURDATE",
      headerName: "תאריך",
      type: "date",
    },
    {
      field: "TOTPRICE",
      headerName: "סכום הזמנה",
      renderCell: (params) => formatNumber(params.value),
    },
    {
      field: "NEO_USERLOGIN",
      headerName: "אחראית גביה",
    },
    {
      field: "BRANCHNAME",
      headerName: "סניף",
    },
    {
      field: "STATUSICON",
      headerName: "משימות להזמנה",
      renderCell: (params) => {
        const currentOrder = DOrders.find(
          (order) => order.ORDNAME === params.row.ORDNAME
        );
        const orderToCheck = currentOrder || params.row;

        const hasTasks =
          Array.isArray(orderToCheck.GENCUSTNOTES_SUBFORM) &&
          orderToCheck.GENCUSTNOTES_SUBFORM.length > 0;

        const icon = hasTasks ? <LaunchIcon /> : <AddTaskIcon />;
        const tooltipText = hasTasks
          ? "הצג משימות"
          : hasFullObligoAccess
            ? "הוסף משימה"
            : "אין הרשאה להוסיף משימה";

        // Disable AddTaskIcon if user doesn't have full access and there are no tasks
        const isDisabled = !hasTasks && !hasFullObligoAccess;

        const handleClick = (event) => {
          event.stopPropagation();
          if (isDisabled) return;

          if (hasTasks) {
            handleOpenPopup(orderToCheck);
          } else {
            handleAddTaskToOrder(orderToCheck);
          }
        };

        return (
          <Tooltip title={tooltipText}>
            <IconButton onClick={handleClick} disabled={isDisabled}>
              {icon}
            </IconButton>
          </Tooltip>
        );
      },
      sortable: false,
      filterable: false,
      minWidth: 100,
      flex: 0.1,
    },
  ];

  const obligoOrders = useMemo(() => {
    return DOrders?.map((row, index) => ({
      ...row,
      id: index,
      CURDATE: new Date(row["CURDATE"]),
    })) || [];
  }, [DOrders]);

  const filters = { BRANCHNAME: "סניף", NEO_USERLOGIN: "" };

  const hasFullObligoAccess = user?.accessRights?.ObligoExceptionPage;

  return (
    <>
      {isLoading ? (
        <Box
          p={4}
          textAlign="center"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="200px"
        >
          <Box sx={{ position: "relative", display: "inline-flex", mb: 2 }}>
            <CircularProgress
              size={60}
              thickness={4}
              sx={{
                color: "#0563B0",
                animation: "pulse 1.5s ease-in-out infinite",
              }}
            />
          </Box>
          <Typography variant="body1" color="#0563B0" fontWeight="medium">
            טוען נתונים...
          </Typography>
        </Box>
      ) : obligoOrders && obligoOrders.length > 0 ? (
        <Box p={3} bgcolor="background.paper" borderRadius={2} boxShadow={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography>
                {dataGridColumns !== "undefined" && (
                  <DataTabs
                    data={obligoOrders}
                    columns={dataGridColumns}
                    TabKey={{ value: "NEO_USERLOGIN", label: "" }}
                    specificValue={user?.priorityUserName}
                    handleRowClick={hasFullObligoAccess ? handleRowClick : undefined}
                  />
                )}
              </Typography>
            </Grid>
          </Grid>
          <AddTaskDialog
            open={addTaskDialogOpen}
            onClose={() => setAddTaskDialogOpen(false)}
            ordname={selectedOrderForTask?.ORDNAME}
            doerName={selectedOrderForTask?.DOER_NAME} // Adjust based on your data structure
            onTaskAdded={handleTaskAdded}
            data={selectedOrderForTask?.GENCUSTNOTES_SUBFORM}
            setOrders={setDOrders}
          />
        </Box>
      ) : (
        <Box
          p={4}
          textAlign="center"
          borderRadius={2}
          bgcolor="#FFF8E1"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow={1}
          sx={{ minHeight: "150px" }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <WarningAmberIcon sx={{ color: "#FF9800", fontSize: 30 }} />
            <Typography variant="h6" color="text.secondary">
              אין נתונים להצגה
            </Typography>
          </Box>
        </Box>
      )}
      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        maxWidth={false}
        PaperProps={{
          style: {
            width: "40vw",
            maxWidth: "50vw",
            maxHeight: "calc(100% - 50px)",
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "left" }}>
          {" "}
          משימות להזמנה מספר {selectedOrderNotes?.ORDNAME}
        </DialogTitle>
        <DialogContent>
          <NotesAccordion
            data={selectedOrderNotes?.GENCUSTNOTES_SUBFORM}
            setOrders={setDOrders}
            ordname={selectedOrderNotes?.ORDNAME}
            doerName={
              selectedOrderNotes?.AGENTCODE
                ? selectedOrderNotes?.DOERNAME
                : null
            }
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ObligoExceptionDetails;